import { t, Trans } from "@lingui/macro";
import { IApiEntitySmpAgSticker, IApiEntitySmpAgStickerpack } from "@smp/api";
import {
  UiButton,
  UiButtonBase,
  UiLink,
  UiTooltip,
  UiTypography,
} from "@smp/ui";
import { dataArraySieve } from "@smp/utils";
import { IconCheck, IconDownload, IconShare } from "@tabler/icons-react";
import { saveAs } from "file-saver";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useSmpAgTranslation } from "@/hooks/use_smp_ag_translation";
import { useQueryPolotnoTagsAll } from "@/queries/polotno/use_query_polotno_tags_all";
import { customGeneratePath } from "@/utils";

import { useContextStickers } from "../../../context";
import classes from "./sticker_info.module.css";

export const StickerInfo = ({
  sticker,
  stickerpack,
}: {
  sticker: IApiEntitySmpAgSticker;
  stickerpack: IApiEntitySmpAgStickerpack;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const getTranslation = useSmpAgTranslation();

  const {
    pathPattern,
    actions: { setSelectedTags },
  } = useContextStickers();

  const {
    data: { normalized: normalizedTags },
  } = useQueryPolotnoTagsAll();

  const [openCopied, setOpenCopied] = useState(false);

  const fallbackStickerName = t`Sticker` + sticker.id;

  const colorTags = useMemo(() => {
    const slugs = sticker?.tags?.filter((tag) => tag.startsWith("color_"));

    return !!slugs?.length && normalizedTags
      ? dataArraySieve(slugs.map((slug) => normalizedTags.get(slug)))
      : undefined;
  }, [normalizedTags, sticker?.tags]);

  const authorTag = useMemo(() => {
    const slug = sticker.tags?.find((tag) => tag.startsWith("author"));

    return slug && normalizedTags ? normalizedTags?.get(slug) : undefined;
  }, [normalizedTags, sticker.tags]);

  const commonTags = useMemo(() => {
    const slugs = sticker.tags?.filter((tag) =>
      ["sticker_type", "actual"].some((tagGroup) => tag.startsWith(tagGroup)),
    );

    return dataArraySieve(
      slugs?.map((slug) => {
        const tag = normalizedTags?.get(slug);

        if (!tag) {
          return undefined;
        }

        return tag;
      }) || [],
    );
  }, [normalizedTags, sticker.tags]);

  const backUrl = useMemo(() => customGeneratePath(pathPattern), [pathPattern]);

  const selectTag = useCallback(
    (slug: string | undefined) => {
      if (!slug) {
        return;
      }

      navigate(backUrl);

      setSelectedTags([slug]);
    },
    [backUrl, navigate, setSelectedTags],
  );

  const tooltipTitle = openCopied ? (
    <Trans>Copied to clipboard</Trans>
  ) : (
    <Trans>Share</Trans>
  );

  const handleShare = useCallback(() => {
    setOpenCopied(true);

    setTimeout(() => setOpenCopied(false), 3000);

    navigator.clipboard.writeText(
      `${window.location.origin}/home/stickers/${sticker.id}`,
    );
  }, [sticker.id]);

  const handleEdit = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      navigate(`/polotno/elements/${sticker.id}`, {
        state: {
          prevPath: location.pathname,
        },
      });
    },
    [location.pathname, navigate, sticker.id],
  );

  const handleDownload = useCallback(() => {
    const filename = `smmplanner_sticker_${sticker.id}`;
    const url = sticker.image_original_url || sticker.json_url;

    if (!url) {
      return;
    }

    saveAs(url, filename);
  }, [sticker.id, sticker.image_original_url, sticker.json_url]);

  return (
    <div className={classes.wrapper}>
      <div>
        <UiTypography component="h1" className={classes.title}>
          {sticker?.name || fallbackStickerName}
        </UiTypography>

        {sticker?.description && (
          <UiTypography className={classes.description}>
            {sticker.description}
          </UiTypography>
        )}

        {authorTag && (
          <UiTypography className={classes.author}>
            <Trans>templates.by_user</Trans>{" "}
            <UiLink
              className={classes.author_link}
              onClick={() => selectTag(authorTag?.slug)}
              component={HelperAdapterLink}
              to={backUrl}
            >
              {getTranslation(authorTag.translations)}
            </UiLink>
          </UiTypography>
        )}

        <div className={classes.features}>
          {[
            <Trans>Free editing</Trans>,
            <Trans>One-click photo background removal</Trans>,
            <Trans>Improvement photo quality</Trans>,
            <Trans>Thousands of unique templates</Trans>,
          ].map((feature, idx) => (
            <div key={idx} className={classes.features__item}>
              <div className={classes.features__item__icn}>
                <IconCheck size={12} stroke={3} />
              </div>

              <UiTypography className={classes.features__item__text}>
                {feature}
              </UiTypography>
            </div>
          ))}
        </div>

        <div className={classes.actions}>
          <UiButton
            variant="outlined"
            onClick={handleDownload}
            className={classes.actions__download}
          >
            <IconDownload />
          </UiButton>

          <UiButton
            variant="contained"
            onMouseDown={handleEdit}
            component={HelperAdapterLink}
            to={`/polotno/elements/${sticker.id}`}
          >
            <Trans>Use in design</Trans>
          </UiButton>

          <UiTooltip
            PopperProps={{
              disablePortal: true,
            }}
            leaveDelay={500}
            title={tooltipTitle}
          >
            <UiButtonBase
              children={<IconShare />}
              onClick={handleShare}
              className={classes.actions__share}
            />
          </UiTooltip>
        </div>
      </div>

      <div className={classes.wrapper__foot}>
        {!!colorTags?.length && (
          <div className={classes.pallete}>
            {colorTags.map((tag) => {
              const parts = tag.slug.split("_");
              const hex = parts.pop() || "ffffff";

              return (
                <UiButtonBase
                  key={tag.slug}
                  className={classes.pallete__item}
                  style={{ background: `#${hex}` }}
                  onClick={() => selectTag(tag.slug)}
                  component={HelperAdapterLink}
                  to={backUrl}
                />
              );
            })}
          </div>
        )}

        <UiTypography className={classes.stickerpack}>
          <span className={classes.stickerpack__label}>
            <Trans>Sticker from the stickerpack</Trans>{" "}
          </span>

          <span className={classes.stickerpack__val}>
            {getTranslation(stickerpack.title)}
          </span>
        </UiTypography>

        {!!commonTags.length && (
          <div className={classes.tags}>
            {commonTags.map((tag) => (
              <UiButton
                key={tag.slug}
                variant="semifilled"
                size="small"
                className={classes.tags__item}
                onClick={() => selectTag(tag.slug)}
                component={HelperAdapterLink}
                to={backUrl}
              >
                {getTranslation(tag.translations)}
              </UiButton>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
